import { createSelector } from 'reselect'

import { AbortError } from '@app/errors/AbortError'

import { sleep } from '@app/utils/sleep'

import { getAbortSignal } from '@app/packages/abortContext/actions'
import type { Result } from '@app/packages/Result/Result'
import { resultError, resultOk, unwrapResult } from '@app/packages/Result/Result'

import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import type { PromiseState } from './promiseState'

export type Loadable<T> = T | LoadRequest

export class LoadableState<T> {
  value: Loadable<T>
  softRequests: ThunkAction<PromiseState>[] = []
  constructor(value: Loadable<T>) {
    this.value = value
  }
}

export class LoadRequest {
  action: ThunkAction<PromiseState>
  constructor(action: ThunkAction<PromiseState>) {
    this.action = action
  }
}

export type ThunkSelectorState<T> = Result<LoadableState<T>>
export type ThunkSelector<T> = (...args: any) => Result<LoadableState<T>>

type LoadableControls = {
  unwrap: <T>(val: Result<LoadableState<T>>) => T
  load: (load: ThunkAction<PromiseState>) => LoadRequest
  soft: (load: ThunkAction<PromiseState>) => void
}

export const isLoaded = <T>(val: Loadable<T>): val is T => {
  return !(val instanceof LoadRequest)
}

export const unwrapLoadableState = <T>(val: LoadableState<T>): T | null => {
  if (val.value instanceof LoadRequest) return null
  return val.value
}

export const createLoadable = <T>(cb: (control: LoadableControls) => Loadable<T>) => {
  const softRequests: ThunkAction<PromiseState>[] = []
  const controls: LoadableControls = {
    unwrap: <T>(val: Result<LoadableState<T>>) => {
      if (val.error) throw val.value
      if (val.value.value instanceof LoadRequest) throw val.value.value
      softRequests.push(...val.value.softRequests)
      return val.value.value
    },
    load: (load: ThunkAction<PromiseState>): LoadRequest => new LoadRequest(load),
    soft: (load: ThunkAction<PromiseState>) => {
      softRequests.push(load)
    },
  }
  try {
    const state = cb(controls)
    const res = resultOk(new LoadableState(state))
    res.value.softRequests = softRequests
    return res
  } catch (e) {
    if (e instanceof LoadRequest) {
      const res = resultOk(new LoadableState<T>(new LoadRequest(e.action)))
      res.value.softRequests = softRequests
      return res
    }
    const res = resultError(e)
    return res
  }
}

export const createStaticLoadableSelector = <T>(value: T) => {
  const loadable = createLoadable(() => value)
  return () => loadable
}

export const compareLoadables = <T>(a: Result<LoadableState<T>>, b: Result<LoadableState<T>>, compareResult: (a: T, b: T) => boolean = (a, b) => a === b) => {
  if (a.error && b.error) return a.value === b.value
  if (a.error && !b.error) return false
  if (!a.error && b.error) return false
  const al = a.value as LoadableState<T>
  const bl = b.value as LoadableState<T>
  if (al.value instanceof LoadRequest && bl.value instanceof LoadRequest) return al.value.action === bl.value.action
  if (al.value instanceof LoadRequest && !(bl.value instanceof LoadRequest)) return false
  if (!(al.value instanceof LoadRequest) && bl.value instanceof LoadRequest) return false
  return compareResult(al.value as T, bl.value as T)
}

export const resolveThunkSelector = <T>(selector: ThunkSelector<T>) =>
  createThunk(async (dispatch, getState) => {
    // Names the load we were last awaiting so the wrapper below is identifiable in Sentry —
    // the selector itself has no name, and the promise id is the only thing that does.
    let pendingId = ''
    try {
      const sig = dispatch(getAbortSignal())
      let lastHash: string | null = null
      let lastUpdateHash: string | null = null
      while (true) {
        await sleep(10, sig)
        const state = unwrapResult(selector(getState()))
        if (state.softRequests.length) {
          const actions = state.softRequests.map(r => dispatch(r))
          pendingId = actions.map(a => a.id).join(', ')
          const hashes = actions
            .map(a => [a.id, a.hash].join(':'))
            .sort((a, b) => a.localeCompare(b))
            .join('|')
          if (lastUpdateHash === hashes) {
            // If the soft requests failed (network error etc.) rather than truly cycling,
            // clear them from cache so they retry next invocation and return the stale value.
            const failedActions = actions.filter(a => a.error.value !== null)
            if (failedActions.length) {
              failedActions.forEach(a => a.clear())
              const value = state.value
              if (!(value instanceof LoadRequest)) return value
            }
            throw new CircularThunkError(`Soft requests did not update state: ${actions.map(a => a.id).join(', ')}`)
          }
          await Promise.all(state.softRequests.map(r => dispatch(r).promise()))
          lastUpdateHash = hashes
          continue
        }
        const value = state.value
        if (value instanceof LoadRequest) {
          const state = dispatch(value.action)
          pendingId = state.id
          if (sig?.aborted) throw new AbortError(`Aborted: ${state.id} ${state.hash}`)
          const hash = [state.id, state.hash].join(':')
          if (lastHash === hash) {
            throw new CircularThunkError(`State loaded yet empty: ${state.id}`)
          }
          lastHash = hash
          await state.promise()
          if (sig?.aborted || state.aborted.value) throw new AbortError(`Aborted: ${state.id} ${state.hash}`)
          if (state.error.value) throw state.error.value
        } else {
          return value
        }
      }
    } catch (e) {
      throw new Error(`resolveThunkSelector failed: ${pendingId || 'unknown'}`, { cause: e })
    }
  })

export const unwrapThunkSelector = <T>(selector: ThunkSelector<T>, label: string) =>
  createThunk((_dispatch, getState) => {
    const val = selector(getState())
    if (val.error) throw val.value
    if (!isLoaded(val.value.value)) throw new Error(label)
    return val.value.value
  })

export const createUnwrappedThunkSelector =
  <T>(selector: ThunkSelector<T>, label: string) =>
  (...args: Parameters<typeof selector>) => {
    const val = selector(...args)
    if (val.error) throw val.value
    if (!isLoaded(val.value.value)) throw new Error(label)
    return val.value.value
  }

export class CircularThunkError extends Error {}

export const mapThunkSelector = <T, S>(selector: ThunkSelector<T>, map: (val: T) => S): ThunkSelector<S> => {
  return createSelector([selector], state =>
    createLoadable(c => {
      const val = c.unwrap(state)
      return map(val)
    })
  )
}
